import { useState } from 'react'

interface Source {
  title: string
  url?: string
  snippet?: string
}

interface RagResponse {
  answer: string
  sources: Source[]
}

const EXAMPLES = [
  'Combien rapporte la TVA en 2023 ?',
  "Quelle est la part de la dette publique dans le PIB français ?",
  'Comment évolue le déficit public depuis 2015 ?',
  "Quels sont les principaux postes de dépenses de l'État ?",
  "Comment la France se compare à l'Allemagne en termes de prélèvements obligatoires ?",
]

export default function Ask() {
  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState<RagResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!question.trim()) return
    setLoading(true)
    setAnswer(null)
    setError('')
    try {
      const res = await fetch('/api/rag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question }),
      })
      if (!res.ok) throw new Error(`Erreur ${res.status}`)
      const data = await res.json() as RagResponse
      setAnswer(data)
    } catch (err) {
      setError(
        'Le service RAG n\'est pas encore déployé. ' +
        'Il nécessite Cloudflare Pages Functions + Vectorize + GLM API.'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Posez votre question</h2>
        <p className="text-text-muted mt-1">
          Interrogez les données budgétaires et fiscales françaises.
          Les réponses sont basées sur les sources Eurostat, INSEE et gouvernementales.
        </p>
      </div>

      {/* Suggested questions */}
      <div className="flex flex-wrap gap-2 mb-6">
        {EXAMPLES.map((ex) => (
          <button
            key={ex}
            onClick={() => setQuestion(ex)}
            className="text-xs bg-card border border-border px-3 py-1.5 rounded-full hover:border-nav hover:text-nav transition-colors"
          >
            {ex}
          </button>
        ))}
      </div>

      {/* Search form */}
      <form onSubmit={handleSubmit} className="mb-8">
        <div className="flex gap-2">
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ex : Combien rapporte la TVA en 2023 ?"
            className="flex-1 rounded-lg border border-input px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
          />
          <button
            type="submit"
            disabled={loading || !question.trim()}
            className="bg-nav text-white px-6 py-3 rounded-lg font-medium text-sm hover:bg-nav-dark disabled:opacity-50 transition-colors"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Analyse…
              </span>
            ) : 'Poser'}
          </button>
        </div>
      </form>

      {/* Error */}
      {error && (
        <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6 text-sm text-amber-800 dark:text-amber-200">
          {error}
        </div>
      )}

      {/* Answer */}
      {answer && (
        <div className="space-y-4">
          <div className="bg-card rounded-lg shadow p-6">
            <div
              className="prose prose-sm max-w-none leading-relaxed"
              dangerouslySetInnerHTML={{ __html: answer.answer }}
            />
          </div>

          {/* Sources */}
          {answer.sources && answer.sources.length > 0 && (
            <div className="bg-card rounded-lg shadow p-6">
              <h3 className="font-semibold text-sm mb-3 text-text-muted uppercase tracking-wide">
                Sources ({answer.sources.length})
              </h3>
              <ul className="space-y-2">
                {answer.sources.map((source, i) => (
                  <li key={i} className="text-sm flex items-start gap-2">
                    <span className="text-nav font-bold">{i + 1}.</span>
                    <div>
                      {source.url ? (
                        <a
                          href={source.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-nav hover:underline"
                        >
                          {source.title}
                        </a>
                      ) : (
                        <span className="font-medium">{source.title}</span>
                      )}
                      {source.snippet && (
                        <p className="text-text-muted text-xs mt-0.5">{source.snippet}</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!answer && !error && !loading && (
        <div className="text-center py-12 text-text-muted">
          <div className="text-4xl mb-3">📊</div>
          <p className="text-sm">
            Cliquez sur une question suggérée ou tapez la vôtre.
          </p>
        </div>
      )}

      <p className="text-xs text-text-muted mt-6">
        Le RAG (Retrieval-Augmented Generation) utilise des embeddings multilingues
        pour rechercher dans les documents officiels, puis génère une réponse
        contextualisée avec citations.
      </p>
    </div>
  )
}
